import React, { useState, useEffect } from "react";
import { postData } from "../../utility/Utility";
import Swal from "sweetalert2";
import CustomLoader from "../../Components/CustomLoader/CustomLoader";
import ReactPaginate from "react-paginate";
import MessageTemplateModal from "../../Common/MessageTemplateModal";
import moment from "moment";
function EventTemplateList() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTemplates, setTotalTemplates] = useState(0);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editMode, setEditMode] = useState(false);

  // Fetch templates from API
  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await postData("api/event-templates/list", {
        limit: 10,
        page: currentPage + 1, // API uses 1-based index
        search: searchTerm.trim()
      });

      if (response?.success) {
        setTemplates(response.data.events || []);
        setTotalPages(response.data.pagination?.totalPages || 1);
        setTotalTemplates(response.data.pagination?.totalCount || 0);
      } else {
        setError(response?.message || "Failed to fetch templates");
        setTemplates([]);
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
      setError("Error fetching templates");
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchTerm]);

  // Handle page change
  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    fetchTemplates();
  };

  // Delete template
  const handleDelete = async (templateId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel"
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      setDeleteLoading(templateId);
      const response = await postData("api/event-templates/delete", { eventId: templateId });

      if (response?.success) {
        Swal.fire({
          title: "Deleted!",
          text: "Template has been deleted successfully.",
          icon: "success",
          timer: 2000,
          showConfirmButton: false
        });
        // Refresh the list
        fetchTemplates();
      } else {
        Swal.fire({
          title: "Error!",
          text: response?.message || "Failed to delete template",
          icon: "error"
        });
      }
    } catch (error) {
      console.error("Error deleting template:", error);
      Swal.fire({
        title: "Error!",
        text: "Error deleting template",
        icon: "error"
      });
    } finally {
      setDeleteLoading(null);
    }
  };

  // Reset all filters
  const handleReset = () => {
    setSearchTerm("");
    setCurrentPage(0);
  };

  // Handle edit template
  const handleEdit = (template) => {
    setSelectedTemplate(template);
    setEditMode(true);
    setIsModalOpen(true);
  };



  // Handle modal close
  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedTemplate(null);
    setEditMode(false);
  };

  // Handle template created/updated
  const handleTemplateSubmit = () => {
    // Refresh the list after template is created or updated
    fetchTemplates();
  };


  if (loading) {
    return (
      <div className="content-page">
        <CustomLoader loading={true} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="content-page">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12">
              <div className="alert alert-danger text-center">
                <i className="mdi mdi-alert-circle me-2"></i>
                {error}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="content-page">
      <CustomLoader loading={loading} />

      <div className="content">
        <div className="container-fluid">
          {/* Header Section */}
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <div className="flex-grow-1">
              <h4 className="fs-18 fw-semibold m-0">Event Templates Management</h4>
              <p className="text-muted mb-0">Total Templates: {totalTemplates}</p>
            </div>
  
          </div>

          {/* Search Section */}
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row g-3 align-items-end">
                      <div className="col-md-12 col-lg-8">
                        <label htmlFor="Search" className="form-label">Search</label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control ps-4"
                            placeholder="Search by title or festival name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          <span className="input-group-text">
                            <i className="mdi mdi-magnify"></i>
                          </span>
                        </div>
                      </div>

                      <div className="col-md-12 col-lg-4 d-flex gap-2 align-items-end">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={loading}
                        >
                          {loading ? (
                            <span className="spinner-border spinner-border-sm me-1"></span>
                          ) : null}
                          Search
                        </button>

                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={handleReset}
                          disabled={loading}
                        >
                          Reset
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>

          {/* Templates Table */}
          <div className="row mt-2">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  {loading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover table-centered mb-0">
                        <thead>
                          <tr>
                            <th>Title</th>
                            <th>Type</th>
                            <th>Date</th>
                            <th>Audience</th>
                            <th>Channel</th>
                            <th>Status</th>
                            <th>Sent Messages</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {templates?.length > 0 ? (
                            templates.map((template) => (
                              <tr key={template._id}>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <div className="flex-shrink-0 me-2">
                                      <div className="avatar-xs">
                                        <div className="avatar-title rounded-circle bg-light text-primary">
                                          {template.title?.charAt(0) || 'T'}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex-grow-1">
                                      <h5 className="fs-14 mb-1">{template.title}</h5>
                                      {template.festivalName && (
                                        <small className="text-muted">
                                          <i className="mdi mdi-calendar-text me-1"></i>
                                          {template.festivalName}
                                        </small>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <span className={`badge ${template.festivalName ? 'bg-info' : 'bg-success'}`}>
                                    {template.festivalName ? 'Festival' : 'Custom'}
                                  </span>
                                </td>
                                <td>{ moment(template.festivalDate).format('DD-MM-YYYY') }</td>
                                <td>
                                  <span className="badge bg-secondary">
                                    {template.audience}
                                  </span>
                                </td>
                                <td>
                                  <span className="badge bg-primary">
                                    {template.sendOption}
                                  </span>
                                </td>
                                <td>
                                  <span className={`badge ${template.isDeleted ? 'bg-danger' : 'bg-success'}`}>
                                    {template.isDeleted ? 'Deleted' : 'Active'}
                                  </span>
                                </td>
                                <td>
                                  <span className="badge bg-info">
                                    {template.sentMessages ? template.sentMessages.length : 0}
                                  </span>
                                </td>
                                <td>
                                  <div className="d-flex gap-2">
                                    <button
                                      className="btn btn-sm btn-primary"
                                      onClick={() => handleEdit(template)}
                                      title="Edit Template"
                                    >
                                      <i className="mdi mdi-pencil"></i> Edit
                                    </button>
                                    {!template.isDeleted && (
                                      <button
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => handleDelete(template._id)}
                                        disabled={deleteLoading === template._id}
                                        title="Delete Template"
                                      >
                                        {deleteLoading === template._id ? (
                                          <div className="spinner-border spinner-border-sm" role="status">
                                            <span className="visually-hidden">Deleting...</span>
                                          </div>
                                        ) : (
                                          <i className="mdi mdi-delete"></i>
                                        )}
                                        Delete
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="8" className="text-center py-4">
                                No templates found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <ReactPaginate
                          previousLabel={<i className="mdi mdi-chevron-left"></i>}
                          nextLabel={<i className="mdi mdi-chevron-right"></i>}
                          breakLabel="..."
                          breakClassName="page-item"
                          breakLinkClassName="page-link"
                          pageCount={totalPages}
                          marginPagesDisplayed={2}
                          pageRangeDisplayed={5}
                          onPageChange={handlePageChange}
                          containerClassName="pagination pagination-rounded justify-content-end mt-3"
                          pageClassName="page-item"
                          pageLinkClassName="page-link"
                          previousClassName="page-item"
                          previousLinkClassName="page-link"
                          nextClassName="page-item"
                          nextLinkClassName="page-link"
                          activeClassName="active"
                          forcePage={currentPage}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Message Template Modal */}
      <MessageTemplateModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        editMode={editMode}
        templateData={selectedTemplate}
        onTemplateCreated={handleTemplateSubmit}
        onTemplateUpdated={handleTemplateSubmit}
      />
    </div>
  );
}

export default EventTemplateList;
